import type { Interaction } from "../../types";
import type { EntityInfo } from "../../hooks/useEntityMap";
import type { SegmentWithId } from "../../hooks/useSegments";
import {
  activityDayKey,
  segmentDayLookup,
  segmentZoneLookup,
  type SegmentDayLookup,
  type SegmentZoneLookup,
} from "../../utils/tripDisplay";

/** Trip-level interaction filter. Each group is OR within itself, AND across
 *  groups (matching the old TripTrackerUI behavior). Drives the itinerary and
 *  the analytics from one place. */
export interface InteractionFilter {
  days: string[];
  segmentIds: string[];
  types: string[];
  /** Container entities (park / hotel / resort) — matches anything under them. */
  locationIds: string[];
  /** Leaf entities (the specific ride / restaurant / etc.) — exact match. */
  activityIds: string[];
}

export const EMPTY_FILTER: InteractionFilter = {
  days: [],
  segmentIds: [],
  types: [],
  locationIds: [],
  activityIds: [],
};

export const CUSTOM_TYPE = "CUSTOM";

export function isFilterEmpty(f: InteractionFilter): boolean {
  return (
    !f.days.length &&
    !f.segmentIds.length &&
    !f.types.length &&
    !f.locationIds.length &&
    !f.activityIds.length
  );
}

/**
 * Which day an activity belongs to, as a filter key.
 *
 * The same day the timeline files it under (`activityDayKey`), so the day
 * chips match the day headings; without that a late-evening activity can be
 * filed under one day in the filter and drawn under another in the list.
 */
export function dayKeyOf(
  i: Interaction,
  onDay?: SegmentDayLookup,
  onZone?: SegmentZoneLookup,
): string | null {
  return activityDayKey(i, onDay, onZone);
}

export function dayLabelOf(key: string): string {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

export function typeOf(i: Interaction, entityDataMap: Record<string, EntityInfo>): string {
  return i.entityId ? entityDataMap[i.entityId]?.type ?? CUSTOM_TYPE : CUSTOM_TYPE;
}

/** Selected segment ids plus all their descendant segments. */
function segmentScopeFor(selected: string[], segments: SegmentWithId[]): Set<string> | null {
  if (selected.length === 0) return null;
  const childrenByParent = new Map<string, string[]>();
  for (const s of segments) {
    if (s.parentSegmentId) {
      const arr = childrenByParent.get(s.parentSegmentId) ?? [];
      arr.push(s.id);
      childrenByParent.set(s.parentSegmentId, arr);
    }
  }
  const scope = new Set<string>();
  const stack = [...selected];
  while (stack.length) {
    const id = stack.pop()!;
    if (scope.has(id)) continue;
    scope.add(id);
    for (const c of childrenByParent.get(id) ?? []) stack.push(c);
  }
  return scope;
}

/** Apply a filter to an interaction list (used by the itinerary + analytics).
 *  `chains` maps a leaf entity id to its ancestor ids (self → root) so a
 *  selected location (container) matches anything logged beneath it. */
export function filterInteractions<T extends Interaction>(
  interactions: T[],
  filter: InteractionFilter,
  entityDataMap: Record<string, EntityInfo>,
  segments: SegmentWithId[],
  chains: Record<string, string[]>,
  tripZone?: string | null,
): T[] {
  if (isFilterEmpty(filter)) return interactions;
  const scope = segmentScopeFor(filter.segmentIds, segments);
  const onDay = segmentDayLookup(segments);
  const onZone = segmentZoneLookup(segments, tripZone ?? undefined);
  return interactions.filter((i) => {
    if (filter.days.length) {
      const k = dayKeyOf(i, onDay, onZone);
      if (!k || !filter.days.includes(k)) return false;
    }
    if (scope && !(i.segmentId && scope.has(i.segmentId))) return false;
    if (filter.types.length && !filter.types.includes(typeOf(i, entityDataMap))) return false;
    if (filter.locationIds.length) {
      const chain = i.entityId ? chains[i.entityId] ?? [i.entityId] : [];
      if (!chain.some((id) => filter.locationIds.includes(id))) return false;
    }
    if (filter.activityIds.length && !(i.entityId && filter.activityIds.includes(i.entityId))) {
      return false;
    }
    return true;
  });
}
